/**
 * Shared layout presets + helpers used by:
 *   - components/LayoutPanel.jsx      (admin editor UI)
 *   - context/SettingsContext.jsx     (runtime CSS variable injection)
 *
 * The layout sub-doc on site_settings lets admins control alignment,
 * max-width, vertical padding, and (for the hero) variant + image
 * position per device. Each layout-able element on the storefront is
 * tagged with a CSS class such as `.layout-page-title`; the rules
 * defined in `index.css` consume the CSS variables we set on <html>.
 */

// Element keys + labels rendered in the admin UI. Order matches a
// natural reading flow (hero → page title → sub-title → section title).
export const LAYOUT_ELEMENTS = [
  {
    key: 'hero',
    label: 'Hero block',
    hint: 'Homepage hero — headline + sub-title + CTAs',
    supportsHero: true,
  },
  {
    key: 'page_title',
    label: 'Page title',
    hint: 'Top H1 on Category, My orders, Track order, etc.',
  },
  {
    key: 'page_subtitle',
    label: 'Page sub-title',
    hint: 'Helper paragraph directly under a page title',
  },
  {
    key: 'section_title',
    label: 'Section title',
    hint: 'Headings inside a page — "Featured products", "All products"…',
  },
];

// Device tabs in the editor. Order matters: first item is the default.
export const LAYOUT_DEVICES = ['desktop', 'mobile'];

export const LAYOUT_ALIGN_OPTIONS = [
  { value: 'left',   label: 'Left'   },
  { value: 'center', label: 'Center' },
  { value: 'right',  label: 'Right'  },
];

export const LAYOUT_HERO_VARIANTS = [
  { value: 'split-right', label: 'Split — image right', hint: 'Default. Text on the left, featured image on the right.' },
  { value: 'split-left',  label: 'Split — image left',  hint: 'Mirror layout: featured image first, then text block.' },
  { value: 'stacked',     label: 'Stacked',             hint: 'Text block above, featured image below. Recommended for mobile.' },
  { value: 'background',  label: 'Image as background', hint: 'Featured image fills the hero block. Text overlays on a tinted backdrop.' },
  { value: 'right-only',  label: 'Hide blue panel',     hint: 'Hides the Hero blue text panel and lets the right column (bestsellers / featured / etc.) span full width.' },
];

// Hero block aspect ratio options — controls the shape of the blue panel.
// "auto" lets the block size to its content (no forced ratio).
export const LAYOUT_HERO_ASPECT_OPTIONS = [
  { value: 'auto',  label: 'Auto',     css: 'auto',  hint: 'Sizes to content. Best when hiding the featured card.' },
  { value: '1/1',   label: 'Square',   css: '1 / 1', hint: 'Default — equal width & height.' },
  { value: '16/9',  label: 'Banner',   css: '16 / 9' },
  { value: '21/9',  label: 'Ultrawide', css: '21 / 9' },
  { value: '4/3',   label: 'Classic',  css: '4 / 3' },
  { value: '3/4',   label: 'Portrait', css: '3 / 4' },
];

// What appears in the hero's RIGHT column. `featured` keeps the legacy
// featured-card / slideshow; `bestsellers` swaps in a compact vertical
// bestsellers slider; `empty` hides the column entirely (text panel
// stretches full width).
export const LAYOUT_HERO_RIGHT_CONTENT_OPTIONS = [
  { value: 'featured',    label: 'Featured card', hint: 'Default — featured product card or slideshow.' },
  { value: 'bestsellers', label: 'Bestsellers',   hint: 'Compact bestseller slider fills the right column.' },
  { value: 'empty',       label: 'Empty',         hint: 'Hide the right column — hero text panel stretches full width.' },
];

// Min / max / step for the sliders. `null` is the default ("no override").
export const LAYOUT_BOUNDS = {
  max_width:      { min: 20,  max: 100, step: 1,  unit: 'rem' },
  padding_top:    { min: 0,   max: 12,  step: 0.25, unit: 'rem' },
  padding_bottom: { min: 0,   max: 12,  step: 0.25, unit: 'rem' },
  hero_height:    { min: 16,  max: 60,  step: 1,  unit: 'rem' },
  hero_max_width: { min: 30,  max: 120, step: 1,  unit: 'rem' },
  hero_inner_padding: { min: 0, max: 6, step: 0.25, unit: 'rem' },
};

// Per-device defaults for every element.
const _nullPerDevice = () => ({ desktop: null, mobile: null });
const _elementDefaults = () => ({
  alignment:      _nullPerDevice(),
  max_width:      _nullPerDevice(),
  padding_top:    _nullPerDevice(),
  padding_bottom: _nullPerDevice(),
});

export const LAYOUT_DEFAULTS = {
  apply_to: ['storefront'],
  // Hero-specific variant + image position. Stored per device.
  hero_variant: { desktop: 'split-right', mobile: 'stacked' },
  // Hero block size controls — applied to the BLUE PANEL itself, not the
  // text inside it. `aspect_ratio` accepts one of LAYOUT_HERO_ASPECT_OPTIONS
  // values (`auto`, `1/1`, `16/9`, …); `height` is a fixed rem cap when the
  // aspect ratio is not enough on its own; `max_width` caps the panel.
  hero_block: {
    aspect_ratio: { desktop: null, mobile: null },
    height:       { desktop: null, mobile: null },
    max_width:    { desktop: null, mobile: null },
    inner_padding: { desktop: null, mobile: null },
  },
  // What sits in the hero's right column. Default `featured` preserves
  // the legacy product/slideshow card. Per device.
  hero_right_content: { desktop: 'featured', mobile: 'featured' },
  elements: LAYOUT_ELEMENTS.reduce((acc, el) => {
    acc[el.key] = _elementDefaults();
    return acc;
  }, {}),
};

/** Resolve raw config from server → fully-populated config. Safe to call
 * with `undefined` or partial data. */
export const resolveLayoutConfig = (raw) => {
  const r = raw || {};
  const elements = {};
  LAYOUT_ELEMENTS.forEach((el) => {
    const e = (r.elements || {})[el.key] || {};
    elements[el.key] = {
      alignment:      { desktop: e.alignment?.desktop      ?? null, mobile: e.alignment?.mobile      ?? null },
      max_width:      { desktop: e.max_width?.desktop      ?? null, mobile: e.max_width?.mobile      ?? null },
      padding_top:    { desktop: e.padding_top?.desktop    ?? null, mobile: e.padding_top?.mobile    ?? null },
      padding_bottom: { desktop: e.padding_bottom?.desktop ?? null, mobile: e.padding_bottom?.mobile ?? null },
    };
  });
  return {
    apply_to: Array.isArray(r.apply_to) ? r.apply_to : LAYOUT_DEFAULTS.apply_to,
    hero_variant: {
      desktop: r.hero_variant?.desktop || LAYOUT_DEFAULTS.hero_variant.desktop,
      mobile:  r.hero_variant?.mobile  || LAYOUT_DEFAULTS.hero_variant.mobile,
    },
    hero_block: {
      aspect_ratio: {
        desktop: r.hero_block?.aspect_ratio?.desktop ?? null,
        mobile:  r.hero_block?.aspect_ratio?.mobile  ?? null,
      },
      height: {
        desktop: r.hero_block?.height?.desktop ?? null,
        mobile:  r.hero_block?.height?.mobile  ?? null,
      },
      max_width: {
        desktop: r.hero_block?.max_width?.desktop ?? null,
        mobile:  r.hero_block?.max_width?.mobile  ?? null,
      },
      inner_padding: {
        desktop: r.hero_block?.inner_padding?.desktop ?? null,
        mobile:  r.hero_block?.inner_padding?.mobile  ?? null,
      },
    },
    hero_right_content: {
      desktop: r.hero_right_content?.desktop || LAYOUT_DEFAULTS.hero_right_content.desktop,
      mobile:  r.hero_right_content?.mobile  || LAYOUT_DEFAULTS.hero_right_content.mobile,
    },
    elements,
  };
};

/** Resolve the effective alignment for an element on a device. Mobile
 * inherits desktop when not set. */
export const resolveAlignment = (cfg, key, device = 'desktop') => {
  const m = cfg?.elements?.[key]?.alignment?.mobile;
  const d = cfg?.elements?.[key]?.alignment?.desktop;
  if (device === 'mobile') return m ?? d ?? null;
  return d ?? null;
};

export const resolveHeroVariant = (cfg, device = 'desktop') => {
  if (device === 'mobile') return cfg?.hero_variant?.mobile || cfg?.hero_variant?.desktop || 'stacked';
  return cfg?.hero_variant?.desktop || 'split-right';
};
